import { useDataProvider, useNotify, type Identifier } from "ra-core";
import { useCallback } from "react";

import { showToast } from "@/components/ui/toast";
import { addDaysKey } from "../../misc/osloDate";
import { useInvalidate } from "../../misc/useInvalidate";
import type { CrmDataProvider } from "../../providers/types";
import type {
  Company,
  CompanyNextStep,
  CompanyNote,
  CompanyProduct,
  Deal,
  LogContactInput,
  MiminNotification,
  WorkItem,
} from "../../types";
import type { DriftScan } from "./buildRows";
import type { FocusRow } from "./ranking";

// Handlingene på radene: fullfør, flytt (én eller alle) og marker varsel som
// lest. Ingenting skrives uten et uttrykt valg; «Flytt alle» kan angres i
// 10 sekunder (M5).

const RESOURCES = [
  "work_items",
  "company_next_steps",
  "company_notes",
  "notifications",
  "deals",
];

const CONTACT_RESOURCES = [
  ...RESOURCES,
  "company_last_contact",
  "customer_timeline",
  "companies",
];

type DealSource = { deal: Deal; next?: WorkItem };

/** Hva en rad har som frist, og hvor den står. */
export type DueTarget =
  | { resource: "work_items"; id: Identifier; field: "due_date" }
  | { resource: "company_next_steps"; id: Identifier; field: "due_date" }
  | { resource: "company_notes"; id: Identifier; field: "follow_up_date" };

export const dueTarget = (row: FocusRow): DueTarget | null => {
  const key = row.key.split(":")[0];
  if (key === "task") {
    return {
      resource: "work_items",
      id: (row.source as WorkItem).id,
      field: "due_date",
    };
  }
  if (key === "step") {
    return {
      resource: "company_next_steps",
      id: (row.source as CompanyNextStep).id,
      field: "due_date",
    };
  }
  if (key === "note") {
    return {
      resource: "company_notes",
      id: (row.source as CompanyNote).id,
      field: "follow_up_date",
    };
  }
  if (key === "deal") {
    const next = (row.source as DealSource).next;
    return next?.due_date && row.type === "next_step"
      ? { resource: "work_items", id: next.id, field: "due_date" }
      : null;
  }
  return null;
};

export const dealOf = (row: FocusRow): Deal | null =>
  row.key.startsWith("deal:") ? (row.source as DealSource).deal : null;

/** Oppgaven bak raden: selve oppgaven eller mulighetens neste steg. */
export const workItemOf = (row: FocusRow): WorkItem | null => {
  if (row.key.startsWith("task:")) return row.source as WorkItem;
  if (row.key.startsWith("deal:"))
    return (row.source as DealSource).next ?? null;
  return null;
};

/** Kan «Ta denne» brukes: lead eller kunde uten eier. */
export const canTake = (row: FocusRow) =>
  !!row.unowned &&
  (row.key.startsWith("deal:") || row.key.startsWith("company:"));

/** Neste mandag etter i dag (Oslo-dato). */
export const nextMonday = (today: string) => {
  const [y, m, d] = today.split("-").map(Number);
  const weekday = (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
  return addDaysKey(today, 7 - weekday);
};

/** Mandag i uken datoen ligger i (Oslo-dato). */
export const weekMonday = (today: string) => {
  const [y, m, d] = today.split("-").map(Number);
  const weekday = (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
  return addDaysKey(today, -weekday);
};

export const QUICK_DATES = (today: string) => [
  { label: "I morgen", date: addDaysKey(today, 1) },
  { label: "Neste mandag", date: nextMonday(today) },
  { label: "Om to uker", date: addDaysKey(today, 14) },
];

export const useFocusActions = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const invalidate = useInvalidate();

  const fail = useCallback(
    (error: unknown) =>
      notify((error as Error)?.message || "ra.notification.http_error", {
        type: "error",
      }),
    [notify],
  );

  const setDue = async (target: DueTarget, date: string | null) => {
    const { data: previous } = await dataProvider.getOne(target.resource, {
      id: target.id,
    });
    await dataProvider.update(target.resource, {
      id: target.id,
      data: { [target.field]: date },
      previousData: previous,
    });
    return (previous as Record<string, string | null>)[target.field] ?? null;
  };

  /** Fullfør: oppgave, neste steg, oppfølging eller varsel. */
  const complete = async (row: FocusRow) => {
    try {
      const kind = row.key.split(":")[0];
      const now = new Date().toISOString();
      if (kind === "task") {
        const task = row.source as WorkItem;
        await dataProvider.update("work_items", {
          id: task.id,
          data: { completed_at: now },
          previousData: task,
        });
      } else if (kind === "deal") {
        const next = (row.source as DealSource).next;
        if (!next) return;
        await dataProvider.update("work_items", {
          id: next.id,
          data: { completed_at: now },
          previousData: next,
        });
      } else if (kind === "step") {
        await dataProvider.completeCompanyNextStep(
          (row.source as CompanyNextStep).id,
        );
      } else if (kind === "note") {
        const note = row.source as CompanyNote;
        await dataProvider.update("company_notes", {
          id: note.id,
          data: { follow_up_date: null },
          previousData: note,
        });
      } else if (kind === "notification") {
        const n = row.source as MiminNotification;
        await dataProvider.update("notifications", {
          id: n.id,
          data: { read_at: now },
          previousData: n,
        });
      } else {
        return;
      }
      await invalidate(...RESOURCES);
      showToast(kind === "notification" ? "Markert som lest" : "Fullført", {
        variant: "success",
        description: row.title,
      });
    } catch (error) {
      fail(error);
    }
  };

  /** Flytt én rad til en ny dato. */
  const move = async (row: FocusRow, date: string) => {
    const target = dueTarget(row);
    if (!target) return;
    try {
      const before = await setDue(target, date);
      await invalidate(...RESOURCES);
      showToast("Flyttet", {
        variant: "success",
        description: row.title,
        duration: 10000,
        action: {
          label: "Angre",
          onClick: () =>
            void setDue(target, before)
              .then(() => invalidate(...RESOURCES))
              .catch(fail),
        },
      });
    } catch (error) {
      fail(error);
    }
  };

  /** «Flytt alle til neste uke»: alle rader med dato, én angre for alle. */
  const moveAll = async (rows: FocusRow[], date: string) => {
    const targets = rows
      .map(dueTarget)
      .filter((t): t is DueTarget => t != null);
    if (!targets.length) return;
    const done: { target: DueTarget; before: string | null }[] = [];
    try {
      for (const target of targets) {
        done.push({ target, before: await setDue(target, date) });
      }
    } catch (error) {
      fail(error);
    }
    await invalidate(...RESOURCES);
    if (!done.length) return;
    showToast(
      done.length === 1 ? "1 ting flyttet" : `${done.length} ting flyttet`,
      {
        variant: "success",
        duration: 10000,
        action: {
          label: "Angre",
          onClick: () =>
            void Promise.all(done.map((d) => setDue(d.target, d.before)))
              .then(() => invalidate(...RESOURCES))
              .catch(fail),
        },
      },
    );
  };

  /** «Registrer kontakt»: kontaktlogg og eventuelt neste steg i ett. */
  const logContact = async (input: LogContactInput) => {
    const result = await dataProvider.logContact(input);
    await invalidate(...CONTACT_RESOURCES);
    return result;
  };

  /** «Ta denne» (fiks 4): brukeren blir eier. Kan angres i 10 sekunder. */
  const take = async (row: FocusRow, me: Identifier) => {
    const resource = row.key.startsWith("deal:") ? "deals" : "companies";
    const record =
      resource === "deals"
        ? (row.source as DealSource).deal
        : (row.source as Company);
    const setOwner = async (salesId: Identifier | null) => {
      const { data: previous } = await dataProvider.getOne(resource, {
        id: record.id,
      });
      await dataProvider.update(resource, {
        id: record.id,
        data: { sales_id: salesId },
        previousData: previous,
      });
      await invalidate(...CONTACT_RESOURCES);
    };
    try {
      await setOwner(me);
      showToast("Du er eier nå", {
        variant: "success",
        description: row.title,
        duration: 10000,
        action: {
          label: "Angre",
          onClick: () => void setOwner(null).catch(fail),
        },
      });
      return true;
    } catch (error) {
      fail(error);
      return false;
    }
  };

  /** «Registrer vedlikehold»: samme som «I dag» i Avtaler. */
  const markMaintained = async (row: FocusRow, today: string) => {
    const agreement = row.source as CompanyProduct;
    try {
      await dataProvider.update<CompanyProduct>("company_products", {
        id: agreement.id,
        data: { last_maintained_on: today },
        previousData: agreement,
      });
      await invalidate("company_products");
      showToast("Vedlikehold registrert i dag", {
        variant: "success",
        description: row.title,
      });
      return true;
    } catch (error) {
      fail(error);
      return false;
    }
  };

  /** «Lag oppgaver for valgte»: én oppgave per funn, uten duplikater. */
  const createDriftTasks = async (
    row: FocusRow,
    findings: string[],
    assignee: Identifier | null,
  ) => {
    const scan = row.source as DriftScan;
    const results = await Promise.allSettled(
      findings.map((finding) =>
        dataProvider.create<WorkItem>("work_items", {
          data: {
            title: finding,
            company_id: scan.companyId,
            assignee_id: assignee,
            drift_finding: finding,
            completed_at: null,
            created_at: new Date().toISOString(),
          },
        }),
      ),
    );
    const created = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.find(
      (r): r is PromiseRejectedResult => r.status === "rejected",
    );
    if (failed) fail(failed.reason);
    await invalidate("work_items");
    return created;
  };

  /**
   * «Lagre fordelingen» (kap. 9): nye frister i én omgang, med én angre.
   * En mulighet uten neste steg får et nytt steg på dagen.
   */
  const saveWeek = async (
    plan: {
      row: FocusRow;
      date: string;
      newStep?: { title: string; taskType: WorkItem["task_type"] };
    }[],
    me: Identifier | null,
  ) => {
    const moved: { target: DueTarget; before: string | null }[] = [];
    const created: WorkItem[] = [];
    try {
      for (const item of plan) {
        const target = dueTarget(item.row);
        if (target) {
          moved.push({ target, before: await setDue(target, item.date) });
          continue;
        }
        const deal = dealOf(item.row);
        if (deal && item.newStep) {
          const { data } = await dataProvider.create<WorkItem>("work_items", {
            data: {
              title: item.newStep.title,
              task_type: item.newStep.taskType,
              due_date: item.date,
              deal_id: deal.id,
              company_id: deal.company_id ?? null,
              assignee_id: deal.sales_id ?? me,
              completed_at: null,
              created_at: new Date().toISOString(),
            },
          });
          created.push(data);
        }
      }
    } catch (error) {
      fail(error);
    }
    await invalidate(...RESOURCES);
    const count = moved.length + created.length;
    if (!count) return 0;
    showToast(
      count === 1 ? "1 ting er planlagt" : `${count} ting er planlagt`,
      {
        variant: "success",
        duration: 10000,
        action: {
          label: "Angre",
          onClick: () =>
            void Promise.all([
              ...moved.map((m) => setDue(m.target, m.before)),
              ...created.map((t) =>
                dataProvider.delete("work_items", {
                  id: t.id,
                  previousData: t,
                }),
              ),
            ])
              .then(() => invalidate(...RESOURCES))
              .catch(fail),
        },
      },
    );
    return count;
  };

  return {
    saveWeek,
    complete,
    move,
    moveAll,
    logContact,
    take,
    markMaintained,
    createDriftTasks,
  };
};
